// Modal dialog on the native <dialog> element: focus is contained and returned. A dialog that is
// mostly reading (shortcut help) can start focus on its title instead of its first control.
import { type ReactNode, useEffect, useRef } from 'react';

export function Dialog({ open, title, onClose, children, footer, labelledBy, className, focusTitle = false }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; labelledBy?: string; className?: string; focusTitle?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const returnTo = useRef<Element | null>(null);
  const headingId = labelledBy ?? `dlg-${title.replace(/\W+/g, '-').toLowerCase()}`;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      returnTo.current = document.activeElement;
      el.showModal();
      if (focusTitle) heading.current?.focus();
    } else if (!open && el.open) {
      el.close();
      if (returnTo.current instanceof HTMLElement) returnTo.current.focus();
    }
  }, [open, focusTitle]);
  return (
    <dialog ref={ref} className={className ? `dialog ${className}` : 'dialog'} aria-labelledby={headingId} onCancel={e => { e.preventDefault(); onClose(); }}>
      <h2 ref={heading} id={headingId} className="dialog-title" tabIndex={focusTitle ? -1 : undefined}>{title}</h2>
      <div className="dialog-body">{children}</div>
      {footer ? <div className="dialog-footer">{footer}</div> : null}
    </dialog>
  );
}
